/**
 * Localizable chrome strings for nadine-hauswirth.com.
 *
 * Flat PascalCase keys, one catalog per locale. German is the source of truth
 * and English is the translation.
 *
 * The catalog deliberately holds CHROME only: navigation, the search overlay,
 * the docs frame, the contact form and the 404 page. It does not hold the
 * site's content. Almost everything a visitor reads on this site comes from a
 * jaen `Field` with a `defaultValue`, and gatsby-source-jaen fans those out
 * per locale, so they stay editable in the CMS in each language instead of
 * being frozen here. Prices.tsx alone has 27 of them.
 *
 * Two things stay out of the catalog on purpose. The practice address is a
 * postal address and reads the same in every language. And the section anchors
 * (`/#change-coaching`, `/#psychotherapie`, `/#preise`) are ids, not copy.
 *
 * Components read this through react-intl, provided by wrapPageElement in
 * gatsby-browser and gatsby-ssr. Keys missing from a locale fall back to the
 * German value, which is what `defaultLocale` below is for.
 */

export type MessageKey = keyof typeof messagesDe;

export const defaultLocale = 'de';

/** Every locale the site is generated in, in menu order. */
export const locales = ['de', 'en'] as const;

export const messagesDe = {
  // Navigation (src/components/navigation/AltTopNav.tsx)
  NavOffer: 'Angebot',
  NavProcess: 'Ablauf',
  NavPractice: 'Praxis',
  NavBlog: 'Blog',
  NavContact: 'Kontakt',
  NavLocationTitle: 'Im Herzen von Wien',
  NavLocationCity: '1030 Wien, Österreich',

  // Docs frame (src/components/AppLayout/DocsLayout.tsx)
  DocsBreadcrumbArticles: 'Artikel',
  DocsMenuSectionMore: 'Mehr',
  DocsMenuHome: 'Hauptseite',
  DocsMenuSectionArticles: 'Blog Artikel',
  TocHeading: 'Inhaltsverzeichnis',

  // Page directory (src/components/navigation/page-directory/PageDirectory.tsx)
  PageDirectorySectionNavigation: 'Navigation',
  NavDocs: 'Dokumentation',
  NavSignIn: 'Anmelden',

  // Search (src/components/search-menu/*)
  SearchButtonLabel: 'Suche',
  SearchModalPlaceholder: 'Suche',
  SearchResultGotoArticle: 'Zum Artikel',
  SearchResultGotoPage: 'Zur Seite',

  // MDX chrome (src/components/main-content/*)
  HeadingAnchorLabel: 'Link zu {title}',
  DocsIndexUntitledPage: 'Seite lesen',
  CodeResultNotRunYet: 'Noch nicht ausgeführt',

  // Contact modal (src/components/ContactModal/ContactModal.tsx)
  ContactModalHeading: 'Kontaktiere mich',
  ContactModalIntro:
    'Ich freue mich über Ihre Nachricht und melde mich schnellstmöglich bei Ihnen.',
  ContactModalFirstNameLabel: 'Vorname',
  ContactModalFirstNamePlaceholder: 'Max',
  ContactModalLastNameLabel: 'Nachname',
  ContactModalLastNamePlaceholder: 'Mustermann',
  ContactModalEmailLabel: 'E-Mail',
  ContactModalEmailPlaceholder: 'max.mustermann@example.com',
  ContactModalPhoneLabel: 'Telefonnummer',
  ContactModalPhonePlaceholder: '+43 123 456 789',
  ContactModalMessageLabel: 'Wie kann ich Ihnen helfen?',
  ContactModalMessagePlaceholder: 'Nachricht',
  ContactModalTerms:
    'Ich bin damit einverstanden, dass meine Angaben zur Kontaktaufnahme und für Rückfragen gespeichert werden.',
  ContactModalTermsRequired:
    'Bitte bestätige die Bedingungen zur Kontaktaufnahme',
  ContactModalSubmit: 'Senden',

  // Contact result (src/services/contact.tsx)
  ContactToastErrorTitle: 'Fehler',
  ContactToastErrorDescription: 'Es ist ein Fehler aufgetreten.',
  ContactToastSuccessTitle: 'Erfolg',
  ContactToastSuccessDescription: 'Ihre Nachricht wurde erfolgreich versendet.',

  // 404 (src/pages/404.tsx)
  NotFoundTitle: 'Seite nicht gefunden',
  NotFoundText: 'Diese Seite existiert nicht.',
  NotFoundBackHome: 'Zurück zur Startseite',

  // Per-route metadata (src/components/Head.tsx). The German homepage already
  // has a title written in the CMS and that one wins; these are the defaults
  // for every route and locale that has none, and any of them can be
  // overridden per locale in the CMS without touching this file.
  //
  // The wording is the practice's own, taken from the page copy. Her
  // professional title in particular is quoted verbatim: "Psychotherapeutin in
  // Ausbildung unter Supervision" is a regulated designation in Austria and is
  // not the same thing as "Psychotherapeutin".
  SiteDescription:
    'Coaching und Psychotherapie in Wien. Nadine Hauswirth, BA. Pth, Psychotherapeutin in Fachausbildung unter Lehrsupervision, Psychoanalyse.',
  PageTitleHome: 'Nadine Hauswirth | Coaching & Psychotherapie in Wien',
  PageDescriptionHome:
    'Coaching und Psychotherapie in Wien. Nadine Hauswirth, BA. Pth, Psychotherapeutin in Fachausbildung unter Lehrsupervision, Psychoanalyse. Praxis in der Löwengasse 28/22, 1030 Wien.',
  PageTitleDocs: 'Blog',
  PageDescriptionDocs:
    'Beiträge zu Psychotherapie, Coaching und dem Umgang mit technologischem Wandel.',
  PageTitleImprint: 'Impressum',
  PageDescriptionImprint:
    'Impressum und Offenlegung nach § 5 ECG und § 25 MedienG.',
  PageTitlePrivacyPolicy: 'Datenschutzerklärung',
  PageDescriptionPrivacyPolicy:
    'Wie personenbezogene Daten auf dieser Website verarbeitet werden.',
  PageTitleTermsOfService: 'Nutzungsbedingungen',
  PageDescriptionTermsOfService:
    'Die Bedingungen für die Nutzung dieser Website.',
  PageTitleNotFound: 'Seite nicht gefunden',
  PageDescriptionNotFound: 'Diese Seite existiert nicht.',

  // Language switcher (src/components/LanguageSwitcher.tsx)
  LanguageSwitcherLabel: 'Sprache wählen',

  // Hero (src/pages/index.tsx). The name and "BA pth." are proper nouns and
  // stay Field defaults in the page itself.
  HeroKicker: 'Psychotherapeutin in Fachausbildung unter Lehrsupervision',
  HeroKickerLocation: 'Wien 3. Bezirk',
  HeroLead:
    'Ich arbeite mit Menschen, die spüren, dass sich etwas wiederholt: im Beruf, in Beziehungen, in sich selbst. Manchmal braucht das einen klaren Fokus und wenige Sitzungen, manchmal Zeit und Tiefe. Beides ist möglich. Meine Praxis liegt in der Löwengasse in Wien-Landstraße, Termine sind auch online möglich.',
  HeroCTA1: 'Termin buchen',
  HeroCTA2: 'Angebot ansehen',

  // Angebot section (src/components/sections/Angebot.tsx)
  AngebotKicker: 'Angebot',
  AngebotTitle: 'Fünf Wege, je nach Anliegen',
  AngebotIntro:
    'Was zu Ihnen passt, klären wir im Erstgespräch. Die Preise gelten pro Einheit, eine Absage ist bis 24 Stunden vorher kostenfrei.',
  Angebot_Psychotherapie_Title: 'Psychotherapie',
  Angebot_Psychotherapie_Text:
    'Für psychische Belastungen mit Krankheitswert: Ängste, Zwänge, Depressionen, psychosomatische Beschwerden, Anpassungsstörungen nach Verlusten oder Veränderungen. Psychoanalytisch orientiert, im verlässlichen Rahmen.',
  Angebot_Psychotherapie_Meta: '50 Minuten',
  Angebot_Psychotherapie_Price: '80 €',
  Angebot_Psychotherapie_More: 'Mehr zur Psychotherapie',
  Angebot_Psychoanalyse_Title: 'Psychoanalyse',
  Angebot_Psychoanalyse_Text:
    'Unbewusstes wird verstehbar. Wir arbeiten am Beziehungsmuster, an den Abwehrmechanismen und Widerständen, die es schützen, an den dahinterliegenden Konflikten und an den Lebensthemen, die sich wiederholen. Sitzend oder auf der Couch, in der Regel mehrmals wöchentlich.',
  Angebot_Psychoanalyse_Meta: '50 Minuten',
  Angebot_Psychoanalyse_Price: '80 €',
  Angebot_Psychoanalyse_More: 'Mehr zur Psychoanalyse',
  Angebot_ChangeCoaching_Title: 'Change Coaching',
  Angebot_ChangeCoaching_Text:
    'Für Menschen ohne Krankheitswert, die sich beruflich oder persönlich neu ausrichten. Umgang mit Leistungsdruck, KI und Beschleunigung, Stressmanagement, Tech-Life-Balance.',
  Angebot_ChangeCoaching_Meta: '50 Minuten',
  Angebot_ChangeCoaching_Price: '120 €',
  Angebot_ChangeCoaching_More: 'Mehr zum Change Coaching',
  Angebot_Pferdecoaching_Title: 'Pferdegestütztes Coaching',
  Angebot_Pferdecoaching_Text:
    'Arbeit am Boden mit einem Pferd als Gegenüber. Für Führung, Auftreten und Selbstregulation. Sie erleben unmittelbar, wie Sie wirken. Keine Reiterfahrung nötig, es wird nicht geritten.',
  Angebot_Pferdecoaching_Meta: '50 Minuten',
  Angebot_Pferdecoaching_Price: '120 €',
  Angebot_Pferdecoaching_More: 'Mehr zum Pferdecoaching',
  Angebot_Abschied_Title: 'Abschied nehmen',
  Angebot_Abschied_Text:
    'Begleitung bei Trauer nach einem Todesfall und bei Trennungen. Auf Wunsch mit Einbezug der Arbeit am Pferd, wenn Sprechen allein gerade nicht trägt.',
  Angebot_Abschied_Meta: '50 Minuten · kurzfristige Termine möglich',
  Angebot_Abschied_Price: '80 €',
  Angebot_Abschied_More: 'Mehr zur Trauerbegleitung',

  // Ablauf section (src/components/sections/Ablauf.tsx)
  AblaufKicker: 'Ablauf',
  AblaufTitle: 'Wie eine Zusammenarbeit beginnt',
  AblaufIntro:
    'Der Ablauf richtet sich nach Ihrem individuellen Anliegen. Diese vier Schritte sind aber immer gleich.',
  Ablauf_Erstgespraech_Title: 'Erstgespräch',
  Ablauf_Erstgespraech_Text:
    '50 Minuten, 45 €. Wir klären Ihr Anliegen und ob ein tragfähiger Rahmen für die gemeinsame Arbeit entstehen kann. In der Praxis oder per Video, ohne weitere Verpflichtung.',
  Ablauf_Rahmen_Title: 'Rahmen festlegen',
  Ablauf_Rahmen_Text:
    'Gemeinsam entscheiden wir über Verfahren, Frequenz und ungefähre Dauer. Ob Therapie oder Coaching, ob wöchentlich oder in größeren Abständen, ob mit oder ohne Pferd.',
  Ablauf_Arbeit_Title: 'Laufende Arbeit',
  Ablauf_Arbeit_Text:
    'Sitzungen zu 50 Minuten in verlässlichem Takt. Zeigt sich unterwegs, dass etwas anderes gebraucht wird, passen wir den Rahmen an.',
  Ablauf_Abschluss_Title: 'Abschluss',
  Ablauf_Abschluss_Text:
    'Das Ende des Coachings und der Therapie wird gemeinsam besprochen und es kann ein Resümee gezogen werden.',
  AblaufOutro: 'Wie ich arbeite und woran, beschreibe ich ausführlich in den',
  AblaufOutroLink1: 'Theoretischen Grundlagen',
  AblaufOutroAnd: 'und unter',
  AblaufOutroLink2: 'Ablauf, Praxis und Kosten',

  // PraxisFacts section (src/components/sections/PraxisFacts.tsx)
  PraxisKicker: 'Praxis',
  PraxisTitle: 'Praxis in Wien-Landstraße',
  Praxis_Beruf_Label: 'Berufsbezeichnung',
  Praxis_Beruf_Value:
    'Psychotherapeutin in Fachausbildung unter Lehrsupervision, Fachrichtung Psychoanalyse',
  Praxis_Ausrichtung_Label: 'Ausrichtung',
  Praxis_Ausrichtung_Value:
    'Psychoanalytisch orientierte Psychotherapie, Psychoanalyse, fokale Kurzzeittherapie',
  Praxis_Coaching_Label: 'Coaching',
  Praxis_Coaching_Value: 'Change Coaching und pferdegestütztes Coaching',
  Praxis_Schwerpunkte_Label: 'Schwerpunkte',
  Praxis_Schwerpunkte_Value:
    'Beziehungsmuster, Ängste, Krisen, Trauer und Trennung, beruflicher Wandel',
  Praxis_Praxis_Label: 'Praxis',
  Praxis_Praxis_Value: 'Löwengasse 28/22, 1030 Wien, Termine auch online',
  Praxis_Sprachen_Label: 'Sprachen',
  Praxis_Sprachen_Value: 'Deutsch, Englisch',
  Praxis_Kontakt_Label: 'Kontakt',
  Praxis_Kontakt_Value: '0660 384 04 88, office@nadine-hauswirth.com',
  PraxisOutro:
    'Wenn ein Weg in die Praxis gerade nicht möglich oder nicht sinnvoll ist, führe ich Termine auch per Video. Das pferdegestützte Coaching findet nach Vereinbarung an einem Reitstall im Umland statt.',
  PraxisOutroLink: 'Mehr erfahren',

  // Termin section and the /termin landing page
  TerminTag: 'Termin',
  TerminHeading: 'Termin anfragen',
  TerminIntro:
    'Sie wählen hier Wunschtermin und Uhrzeit, ich bestätige Ihnen den Platz persönlich per E-Mail, meist innerhalb eines Werktags. Eine Absage ist bis 24 Stunden vorher kostenfrei.',
  TerminStep1: 'Art des Termins',
  TerminStep2: 'Tag und Uhrzeit',
  TerminType_erstgespraech_Title: 'Erstgespräch',
  TerminType_erstgespraech_Meta: '50 Min · Kennenlernen',
  TerminType_psychotherapie_Title: 'Psychotherapie',
  TerminType_psychotherapie_Meta: '50 Min · Einzelsitzung',
  TerminType_coaching_Title: 'Coaching',
  TerminType_coaching_Meta: '50 Min · auch mit Pferd',
  TerminSummaryType: 'Termin',
  TerminSummaryDate: 'Datum',
  TerminSummaryCost: 'Kosten',
  TerminSubmit: 'Termin anfragen',
  TerminSubmitHint: 'Der Termin wird erst mit meiner persönlichen Bestätigung fix.',
  TerminPickHint:
    'Freie Tage sind mit einem Punkt markiert. Wählen Sie einen Tag, um die Uhrzeiten zu sehen.',
  TerminPrevMonth: 'Voriger Monat',
  TerminNextMonth: 'Nächster Monat',
  TerminOClock: 'Uhr',
  TerminRequestPrefix: 'Terminanfrage',
  TerminRequestWish: 'Wunschtermin',
  PageTitleTermin: 'Termin anfragen | Nadine Hauswirth, Wien',
  PageDescriptionTermin:
    'Wunschtermin für Erstgespräch, Psychotherapie oder Coaching in Wien anfragen. Persönliche Bestätigung per E-Mail, Absage bis 24 Stunden vorher kostenfrei.',
  PageTitleTerminDanke: 'Anfrage gesendet | Nadine Hauswirth',
  PageDescriptionTerminDanke:
    'Ihre Terminanfrage ist eingegangen. Sie erhalten in Kürze eine persönliche Bestätigung per E-Mail.',
  TerminDankeHeading: 'Danke für Ihre Anfrage',
  TerminDankeText:
    'Ihre Terminanfrage ist bei mir eingegangen. Ich melde mich innerhalb von zwei Werktagen persönlich per E-Mail und bestätige Ihnen den Termin oder schlage eine Alternative vor.',
  TerminDankeBack: 'Zur Startseite',
  TerminCTAText:
    'Wählen Sie Wunschtermin und Uhrzeit direkt online. Ich bestätige Ihnen den Platz persönlich per E-Mail, meist innerhalb eines Werktags.',
  TerminCTAButton: 'Zur Terminanfrage',
  AngebotCardCTA: 'Termin buchen',

  // Psychotherapie section (src/components/sections/Psychotherapie.tsx)
  PsychoTag: 'Psychotherapie',
  PsychoHeadline: 'Psychoanalytisch orientierte Psychotherapie',
  PsychoIntro:
    'Psychoanalytisch orientierte Psychotherapie unterstützt Sie dabei, <b>unbewusste Dynamiken zu verstehen</b> und Ihren Handlungsspielraum zu erweitern. In einem geschützten, verlässlichen Rahmen arbeiten wir an den Mustern, die Ihren Alltag, Ihre Beziehungen und Ihre Lebensgestaltung prägen, mit dem Ziel von mehr Selbstbestimmtheit und innerer Klarheit.',
  PsychoImageAlt: 'Die Praxis von Nadine Hauswirth',
  PsychoFormsHeading: 'Therapieformen',
  Psycho_Form1_Label: 'Psychoanalyse:',
  Psycho_Form1_Text:
    'Unbewusstes wird verstehbar, mittels freier Assoziation und gemeinsamer Arbeit an Abwehrmechanismen. Sitzend oder auf der Couch, in der Regel <em>mehrmals wöchentlich</em>.',
  Psycho_Form2_Label: 'Psychoanalytisch orientierte Psychotherapie:',
  Psycho_Form2_Text:
    'Sitzend, <em>ein- bis zweimal pro Woche</em>. Mentalisierungsbasiert mit Fokus auf Ihr Beziehungsmuster: Wir verstehen, wie Auslöser zu wiederkehrenden Konflikten in Beruf, Alltag und Partnerschaft führen, und verändern das.',
  Psycho_Form3_Label: 'Fokale Kurzzeittherapie:',
  Psycho_Form3_Text:
    'Ziel- und lösungsorientiert für klar umrissene Anliegen. Wir arbeiten fokussiert und zeitlich begrenzt, damit ein konkretes Problem bestmöglich gelöst werden kann.',
  Psycho_Form4_Label: 'Krisenbewältigung:',
  Psycho_Form4_Text:
    'Akute Belastungen ordnen, Einflussfaktoren benennen, Klarheit gewinnen und tragfähige Schritte entwickeln, um die aktuelle Situation zu bewältigen.',
  PsychoExamplesHeading: 'Ich behandle, zum Beispiel',
  Psycho_Beispiel1: 'Angststörungen &amp; Zwangsstörungen',
  Psycho_Beispiel2: 'Depressionen &amp; Trauerbegleitung',
  Psycho_Beispiel3: 'Psychosomatische Beschwerden',
  Psycho_Beispiel4: 'Prokrastination &amp; Verhaltenssüchte',
  Psycho_Beispiel5: 'Autismus-Spektrum-Störung (ASS)',
  Psycho_Beispiel6: 'Anpassungsstörungen nach Verlusten und Veränderungen',
  PsychoFacts:
    'Psychotherapeutin in Fachausbildung unter Lehrsupervision, Fachrichtung Psychoanalyse. Sitzungen zu 50 Minuten, 80 € pro Einheit, Erstgespräch 45 €. Praxis: Löwengasse 28/22, 1030 Wien, Termine auch online.',
  PsychoCTA1: 'Erstgespräch vereinbaren',
  PsychoCTA2: 'Mehr zur Psychotherapie',

  // Change Coaching section (src/components/sections/ChangeCoaching.tsx)
  ChangeTag: 'Change Coaching',
  ChangeHeadline: 'Wandel meistern, nicht nur mithalten',
  ChangeIntro:
    'Die Welt wird immer komplexer und Arbeit zunehmend abstrakt. Auch gesunde Menschen erleben heute enorme psychische Belastungen. Ich helfe Ihnen, mit dem Leistungsdruck des 21. Jahrhunderts umzugehen, und begleite Sie bei <b>Herausforderungen</b> und <b>Möglichkeiten</b>. In Kooperation mit technischen Consultants schaffen wir Klarheit rund um KI, Tools und Prozesse.',
  ChangeSpecialsHeading: 'Meine Spezialitäten',
  Change_ChangeFeat1: 'KI-Leistungsdruck',
  Change_ChangeFeat2: 'Technophobie',
  Change_ChangeFeat3: 'Stressmanagement',
  Change_ChangeFeat4: 'Social-Media-Sucht / Digital Detox',
  Change_ChangeFeat5: 'Impostor-Syndrom',
  Change_ChangeFeat6: 'Tech-Life-Balance',
  ChangeFacts:
    'Coaching-Einheiten zu 50 Minuten, 120 € pro Einheit, Erstgespräch 45 €. In der Praxis, online oder mit dem Pferd.',
  ChangeCTA1: 'Termin buchen',
  ChangeCTA2: 'Mehr erfahren',
  ChangeImageAlt: 'Change Coaching bei Nadine Hauswirth',

  // FAQ section (src/components/sections/FAQ.tsx)
  FAQ_Tag: 'FAQ',
  FAQ_Title: 'Häufige Fragen (FAQ)',
  FAQ_Q1: 'Worin liegt der Unterschied zwischen Coaching und Psychotherapie?',
  FAQ_A1:
    '<b>Coaching</b> richtet sich an nicht-krankhafte Anliegen: Leistung, Fokus, Entscheidungen, Rollenwechsel, Umgang mit Technik (z. B. KI-Leistungsdruck) oder Work-/Tech-Life-Balance. Ziel ist Klarheit, Ergebnisfokus und Verhaltenstraining im Alltag.<br/><br/><b>Psychotherapie</b> setze ich ein, wenn Symptome <em>Krankheitswert</em> haben (z. B. Ängste, Depression, Zwangssymptome, psychosomatische Beschwerden, anhaltende Krisen). Hier arbeite ich tiefenpsychologisch fundiert an unbewussten Dynamiken, Beziehungsmustern und einer nachhaltigen Erweiterung Ihres Handlungsspielraums.',
  FAQ_Q2: 'Wann empfehle ich Coaching und wann Psychotherapie?',
  FAQ_A2:
    '<b>Coaching</b>: Sie sind grundsätzlich psychisch stabil, möchten aber schneller, strukturiert und gezielt an Zielen arbeiten, zum Beispiel mit KI-Tools souverän umgehen, Impostor-Gefühle einordnen oder digitale Routinen (Digital Detox) etablieren.<br/><br/><b>Psychotherapie</b>: Wenn Symptome Sie deutlich einschränken (Schlaf, Appetit, Stimmung, Ängste, Zwänge, psychosomatische Beschwerden) oder Konflikte immer wiederkehren. Ich kläre das mit Ihnen im Erstgespräch und empfehle den passenden Rahmen.',
  FAQ_Q3: 'Wie oft finden die Sitzungen statt?',
  FAQ_A3:
    '<b>Coaching</b>: flexibel, meist alle ein bis drei Wochen, je nach Ziel und Tempo.<br/><br/><b>Psychotherapie</b>: in der Regel ein- bis zweimal pro Woche (bei Psychoanalyse auch häufiger). Kontinuität ist hier Teil der Wirksamkeit.',
  FAQ_Q4: 'Gilt die Schweigepflicht auch im Coaching?',
  FAQ_A4:
    'Ja. <b>Schweigepflicht</b> ist für mich Standard, in der <em>Psychotherapie ebenso wie im Coaching</em>. Diese Vertraulichkeit schafft den sicheren Rahmen, der mich von vielen Coaches abhebt: Sie können offen sprechen, ohne dass Inhalte nach außen gelangen.',
  FAQ_Q5: 'Einzel oder Gruppe: Was biete ich an?',
  FAQ_A5:
    'Ich arbeite im <b>Einzel-Setting</b> und biete auch <b>Gruppen</b> an. Für bestimmte Themen (z. B. Prokrastination oder Digital Detox) kann Gruppe sehr wirksam sein.',
  FAQ_Q6: 'Was passiert im Erstgespräch?',
  FAQ_A6:
    'Wir klären Ihr Anliegen, Ihre Ziele und ob Coaching oder Psychotherapie sinnvoll ist. Sie erhalten eine Empfehlung zum Rahmen mit Frequenz und Vorgehen, transparent und ohne Verpflichtung.',
  FAQ_Note:
    'Hinweis: Coaching ist <em>keine</em> Psychotherapie und ersetzt bei krankheitswertigen Symptomen keine Behandlung. Im Zweifel klären wir im Erstgespräch gemeinsam, welcher Rahmen passt.',

  // NonDisclosure section (src/components/sections/NonDisclosure.tsx)
  ND_Tag: 'Schweigepflicht',
  ND_Heading: 'Vertraulichkeit &amp; Schutz Ihrer Privatsphäre',
  ND_Intro:
    'Die Schweigepflicht gilt bei mir <b>vom ersten bis zum letzten gewechselten Wort</b>, unabhängig davon, ob wir persönlich, telefonisch oder schriftlich kommunizieren. Ich lebe sie sowohl in der Psychotherapie als auch im Coaching. <b>Weil Vertrauen ist die Grundlage für jede gute Zusammenarbeit.</b>',
  ND_List1:
    'Keine Weitergabe von Inhalten an Dritte ohne Ihre ausdrückliche, vorherige Zustimmung.',
  ND_List2: 'Gleiche Standards für Psychotherapie <b>und</b> Coaching.',
  ND_CTA1: 'Erstgespräch vereinbaren',
  ND_CTA2: 'Mehr erfahren',
  ND_ImageAlt: 'Nadine Hauswirth bei der Arbeit am Boden mit einem Pferd',

  // Blog slider (src/components/sections/BlogSlider.tsx)
  Blog_Tag: 'Blog',
  Blog_Title: 'Neues aus dem Blog',
  Blog_Empty: 'Keine Blogbeiträge gefunden.',
  Blog_Prev: 'Vorheriger',
  Blog_Next: 'Nächster',

  // Achievement counter (src/components/sections/AchivementCounter.tsx,
  // item labels passed as message ids from src/pages/index.tsx)
  Counter_Tag: 'Change Coaching',
  Counter_Headline: 'Langjährige Erfahrung mit Therapie und Coaching',
  Counter_Years: 'Jahre',
  Counter_Therapies: 'Therapien',
  Counter_Coachings: 'Coachings',
};

export const messagesEn: Partial<typeof messagesDe> = {
  NavOffer: 'Offer',
  NavProcess: 'Process',
  NavPractice: 'Practice',
  NavBlog: 'Blog',
  NavContact: 'Contact',
  NavLocationTitle: 'In the heart of Vienna',
  NavLocationCity: '1030 Vienna, Austria',

  DocsBreadcrumbArticles: 'Articles',
  DocsMenuSectionMore: 'More',
  DocsMenuHome: 'Home',
  DocsMenuSectionArticles: 'Blog articles',
  TocHeading: 'Table of contents',

  PageDirectorySectionNavigation: 'Navigation',
  NavDocs: 'Documentation',
  NavSignIn: 'Sign in',

  SearchButtonLabel: 'Search',
  SearchModalPlaceholder: 'Search',
  SearchResultGotoArticle: 'Go to article',
  SearchResultGotoPage: 'Go to page',

  HeadingAnchorLabel: 'Link to {title}',
  DocsIndexUntitledPage: 'Read page',
  CodeResultNotRunYet: 'Not run yet',

  ContactModalHeading: 'Get in touch',
  ContactModalIntro:
    'I look forward to your message and will get back to you as soon as I can.',
  ContactModalFirstNameLabel: 'First name',
  ContactModalFirstNamePlaceholder: 'Max',
  ContactModalLastNameLabel: 'Last name',
  ContactModalLastNamePlaceholder: 'Mustermann',
  ContactModalEmailLabel: 'Email',
  ContactModalEmailPlaceholder: 'max.mustermann@example.com',
  ContactModalPhoneLabel: 'Phone number',
  ContactModalPhonePlaceholder: '+43 123 456 789',
  ContactModalMessageLabel: 'How can I help you?',
  ContactModalMessagePlaceholder: 'Message',
  ContactModalTerms:
    'I agree that my details may be stored so that I can be contacted and asked follow-up questions.',
  ContactModalTermsRequired: 'Please confirm the terms for getting in touch',
  ContactModalSubmit: 'Send',

  ContactToastErrorTitle: 'Error',
  ContactToastErrorDescription: 'Something went wrong.',
  ContactToastSuccessTitle: 'Sent',
  ContactToastSuccessDescription: 'Your message was sent successfully.',

  NotFoundTitle: 'Page not found',
  NotFoundText: 'This page does not exist.',
  NotFoundBackHome: 'Back to the home page',

  SiteDescription:
    'Coaching and psychotherapy in Vienna. Nadine Hauswirth, BA. Pth, psychotherapist in specialist training under teaching supervision, psychoanalysis.',
  PageTitleHome: 'Nadine Hauswirth | Coaching & Psychotherapy in Vienna',
  PageDescriptionHome:
    'Coaching and psychotherapy in Vienna. Nadine Hauswirth, BA. Pth, psychotherapist in specialist training under teaching supervision, psychoanalysis. Practice at Löwengasse 28/22, 1030 Vienna.',
  PageTitleDocs: 'Blog',
  PageDescriptionDocs:
    'Writing on psychotherapy, coaching and living with technological change.',
  PageTitleImprint: 'Imprint',
  PageDescriptionImprint:
    'Imprint and disclosure under § 5 ECG and § 25 MedienG.',
  PageTitlePrivacyPolicy: 'Privacy policy',
  PageDescriptionPrivacyPolicy:
    'How personal data is processed on this website.',
  PageTitleTermsOfService: 'Terms of service',
  PageDescriptionTermsOfService: 'The terms for using this website.',
  PageTitleNotFound: 'Page not found',
  PageDescriptionNotFound: 'This page does not exist.',

  LanguageSwitcherLabel: 'Choose language',

  // Hero
  HeroKicker: 'Psychotherapist in specialist training under supervision',
  HeroKickerLocation: 'Vienna, 3rd district',
  HeroLead:
    'I work with people who sense that something keeps repeating itself: at work, in relationships, within themselves. Sometimes that calls for a clear focus and a few sessions, sometimes for time and depth. Both are possible. My practice is on Löwengasse in Vienna-Landstraße, and appointments are also available online.',
  HeroCTA1: 'Book an appointment',
  HeroCTA2: 'View services',

  // Angebot section
  AngebotKicker: 'Services',
  AngebotTitle: 'Five paths, depending on your concern',
  AngebotIntro:
    'What suits you best is something we clarify in a first session. Prices are per session, and cancellation is free up to 24 hours in advance.',
  Angebot_Psychotherapie_Title: 'Psychotherapy',
  Angebot_Psychotherapie_Text:
    'For psychological distress of clinical relevance: anxiety, compulsions, depression, psychosomatic complaints, adjustment difficulties after loss or change. Psychoanalytically oriented, within a reliable framework.',
  Angebot_Psychotherapie_Meta: '50 minutes',
  Angebot_Psychotherapie_Price: '80 €',
  Angebot_Psychotherapie_More: 'More about psychotherapy',
  Angebot_Psychoanalyse_Title: 'Psychoanalysis',
  Angebot_Psychoanalyse_Text:
    'The unconscious becomes understandable. We work on relationship patterns, on the defenses and resistances that protect them, on the conflicts behind them and on the life themes that keep returning. Sitting or on the couch, usually several times a week.',
  Angebot_Psychoanalyse_Meta: '50 minutes',
  Angebot_Psychoanalyse_Price: '80 €',
  Angebot_Psychoanalyse_More: 'More about psychoanalysis',
  Angebot_ChangeCoaching_Title: 'Change coaching',
  Angebot_ChangeCoaching_Text:
    'For people without a clinical condition who are reorienting professionally or personally. Dealing with performance pressure, AI and acceleration, stress management, tech-life balance.',
  Angebot_ChangeCoaching_Meta: '50 minutes',
  Angebot_ChangeCoaching_Price: '120 €',
  Angebot_ChangeCoaching_More: 'More about change coaching',
  Angebot_Pferdecoaching_Title: 'Horse-assisted coaching',
  Angebot_Pferdecoaching_Text:
    'Groundwork with a horse as your counterpart. For leadership, presence and self-regulation. You experience directly how you come across. No riding experience needed, and no riding involved.',
  Angebot_Pferdecoaching_Meta: '50 minutes',
  Angebot_Pferdecoaching_Price: '120 €',
  Angebot_Pferdecoaching_More: 'More about horse-assisted coaching',
  Angebot_Abschied_Title: 'Saying goodbye',
  Angebot_Abschied_Text:
    'Support through grief after a death and through separations. On request with the horse involved, when talking alone does not carry you through.',
  Angebot_Abschied_Meta: '50 minutes · short-notice appointments possible',
  Angebot_Abschied_Price: '80 €',
  Angebot_Abschied_More: 'More about grief support',

  // Ablauf section
  AblaufKicker: 'Process',
  AblaufTitle: 'How a collaboration begins',
  AblaufIntro:
    'The process follows your individual concern. These four steps, however, are always the same.',
  Ablauf_Erstgespraech_Title: 'First session',
  Ablauf_Erstgespraech_Text:
    '50 minutes, 45 €. We clarify your concern and whether a solid framework for working together can emerge. At the practice or by video, with no further obligation.',
  Ablauf_Rahmen_Title: 'Setting the framework',
  Ablauf_Rahmen_Text:
    'Together we decide on the method, the frequency and the approximate duration. Therapy or coaching, weekly or at longer intervals, with or without the horse.',
  Ablauf_Arbeit_Title: 'Ongoing work',
  Ablauf_Arbeit_Text:
    'Sessions of 50 minutes at a reliable rhythm. If it turns out along the way that something else is needed, we adjust the framework.',
  Ablauf_Abschluss_Title: 'Completion',
  Ablauf_Abschluss_Text:
    'The end of coaching or therapy is discussed together, with room to draw a resume of the work.',
  AblaufOutro: 'How I work, and on what, is described in detail in the',
  AblaufOutroLink1: 'theoretical foundations',
  AblaufOutroAnd: 'and under',
  AblaufOutroLink2: 'process, practice and costs',

  // PraxisFacts section
  PraxisKicker: 'Practice',
  PraxisTitle: 'Practice in Vienna-Landstraße',
  Praxis_Beruf_Label: 'Professional title',
  Praxis_Beruf_Value:
    'Psychotherapist in specialist training under teaching supervision, specialising in psychoanalysis',
  Praxis_Ausrichtung_Label: 'Orientation',
  Praxis_Ausrichtung_Value:
    'Psychoanalytically oriented psychotherapy, psychoanalysis, focal short-term therapy',
  Praxis_Coaching_Label: 'Coaching',
  Praxis_Coaching_Value: 'Change coaching and horse-assisted coaching',
  Praxis_Schwerpunkte_Label: 'Focus areas',
  Praxis_Schwerpunkte_Value:
    'Relationship patterns, anxiety, crises, grief and separation, professional change',
  Praxis_Praxis_Label: 'Practice',
  Praxis_Praxis_Value: 'Löwengasse 28/22, 1030 Vienna, appointments also online',
  Praxis_Sprachen_Label: 'Languages',
  Praxis_Sprachen_Value: 'German, English',
  Praxis_Kontakt_Label: 'Contact',
  Praxis_Kontakt_Value: '+43 660 384 04 88, office@nadine-hauswirth.com',
  PraxisOutro:
    'If coming to the practice is not possible or not sensible right now, I also hold sessions by video. Horse-assisted coaching takes place by arrangement at a stable just outside Vienna.',
  PraxisOutroLink: 'Learn more',

  // Termin section and the /termin landing page
  TerminTag: 'Appointment',
  TerminHeading: 'Request an appointment',
  TerminIntro:
    'Choose your preferred date and time here. I confirm your slot personally by email, usually within one working day. Cancellation is free up to 24 hours in advance.',
  TerminStep1: 'Type of appointment',
  TerminStep2: 'Day and time',
  TerminType_erstgespraech_Title: 'First session',
  TerminType_erstgespraech_Meta: '50 min · getting to know each other',
  TerminType_psychotherapie_Title: 'Psychotherapy',
  TerminType_psychotherapie_Meta: '50 min · individual session',
  TerminType_coaching_Title: 'Coaching',
  TerminType_coaching_Meta: '50 min · also with the horse',
  TerminSummaryType: 'Appointment',
  TerminSummaryDate: 'Date',
  TerminSummaryCost: 'Cost',
  TerminSubmit: 'Request appointment',
  TerminSubmitHint: 'The appointment becomes final only with my personal confirmation.',
  TerminPickHint:
    'Available days are marked with a dot. Choose a day to see the times.',
  TerminPrevMonth: 'Previous month',
  TerminNextMonth: 'Next month',
  TerminOClock: '',
  TerminRequestPrefix: 'Appointment request',
  TerminRequestWish: 'Preferred slot',
  PageTitleTermin: 'Request an appointment | Nadine Hauswirth, Vienna',
  PageDescriptionTermin:
    'Request your preferred slot for a first session, psychotherapy or coaching in Vienna. Personal confirmation by email, free cancellation up to 24 hours in advance.',
  PageTitleTerminDanke: 'Request sent | Nadine Hauswirth',
  PageDescriptionTerminDanke:
    'Your appointment request has been received. You will shortly get a personal confirmation by email.',
  TerminDankeHeading: 'Thank you for your request',
  TerminDankeText:
    'Your appointment request has reached me. I will get back to you personally by email within two working days to confirm the appointment or suggest an alternative.',
  TerminDankeBack: 'Back to the home page',
  TerminCTAText:
    'Choose your preferred date and time online. I confirm your slot personally by email, usually within one working day.',
  TerminCTAButton: 'Request an appointment',
  AngebotCardCTA: 'Book appointment',

  // Psychotherapie section
  PsychoTag: 'Psychotherapy',
  PsychoHeadline: 'Psychoanalytically oriented psychotherapy',
  PsychoIntro:
    'Psychoanalytically oriented psychotherapy supports you in <b>understanding unconscious dynamics</b> and widening your room to act. Within a protected, reliable framework we work on the patterns shaping your everyday life, your relationships and the way you lead your life, aiming at more self-determination and inner clarity.',
  PsychoImageAlt: 'The practice of Nadine Hauswirth',
  PsychoFormsHeading: 'Forms of therapy',
  Psycho_Form1_Label: 'Psychoanalysis:',
  Psycho_Form1_Text:
    'The unconscious becomes understandable, through free association and joint work on defense mechanisms. Sitting or on the couch, usually <em>several times a week</em>.',
  Psycho_Form2_Label: 'Psychoanalytically oriented psychotherapy:',
  Psycho_Form2_Text:
    'Sitting, <em>once or twice a week</em>. Mentalization-based with a focus on your relationship patterns: we understand how triggers lead to recurring conflicts at work, in daily life and in partnership, and change that.',
  Psycho_Form3_Label: 'Focal short-term therapy:',
  Psycho_Form3_Text:
    'Goal- and solution-oriented for clearly outlined concerns. We work focused and within a limited time, so one concrete problem gets resolved as well as possible.',
  Psycho_Form4_Label: 'Crisis support:',
  Psycho_Form4_Text:
    'Sorting acute strain, naming the factors at play, gaining clarity and developing solid steps to master the current situation.',
  PsychoExamplesHeading: 'What I treat, for example',
  Psycho_Beispiel1: 'Anxiety &amp; obsessive-compulsive disorders',
  Psycho_Beispiel2: 'Depression &amp; grief support',
  Psycho_Beispiel3: 'Psychosomatic complaints',
  Psycho_Beispiel4: 'Procrastination &amp; behavioral addictions',
  Psycho_Beispiel5: 'Autism spectrum condition',
  Psycho_Beispiel6: 'Adjustment difficulties after loss and change',
  PsychoFacts:
    'Psychotherapist in specialist training under teaching supervision, specialising in psychoanalysis. Sessions of 50 minutes, 80 € per session, first session 45 €. Practice: Löwengasse 28/22, 1030 Vienna, appointments also online.',
  PsychoCTA1: 'Arrange a first session',
  PsychoCTA2: 'More about psychotherapy',

  // Change Coaching section
  ChangeTag: 'Change coaching',
  ChangeHeadline: 'Mastering change, not just keeping up',
  ChangeIntro:
    'The world keeps getting more complex and work more abstract. Healthy people also experience enormous psychological strain today. I help you deal with the performance pressure of the 21st century and accompany you through <b>challenges</b> and <b>opportunities</b>. In cooperation with technical consultants we create clarity around AI, tools and processes.',
  ChangeSpecialsHeading: 'My specialties',
  Change_ChangeFeat1: 'AI performance pressure',
  Change_ChangeFeat2: 'Technophobia',
  Change_ChangeFeat3: 'Stress management',
  Change_ChangeFeat4: 'Social media addiction / digital detox',
  Change_ChangeFeat5: 'Impostor syndrome',
  Change_ChangeFeat6: 'Tech-life balance',
  ChangeFacts:
    'Coaching sessions of 50 minutes, 120 € per session, first session 45 €. At the practice, online or with the horse.',
  ChangeCTA1: 'Book appointment',
  ChangeCTA2: 'Learn more',
  ChangeImageAlt: 'Change coaching with Nadine Hauswirth',

  // FAQ section (src/components/sections/FAQ.tsx)
  FAQ_Tag: 'FAQ',
  FAQ_Title: 'Frequently asked questions (FAQ)',
  FAQ_Q1: 'What is the difference between coaching and psychotherapy?',
  FAQ_A1:
    '<b>Coaching</b> addresses concerns that are not illness related: performance, focus, decisions, changing roles, dealing with technology (e.g. AI performance pressure) or work/tech-life balance. The aim is clarity, a focus on results and training new behaviour in everyday life.<br/><br/><b>Psychotherapy</b> is what I use when symptoms have <em>clinical significance</em> (e.g. anxiety, depression, obsessive-compulsive symptoms, psychosomatic complaints, persistent crises). Here I work on a depth-psychological basis with unconscious dynamics, relationship patterns and a lasting expansion of your scope for action.',
  FAQ_Q2: 'When do I recommend coaching and when psychotherapy?',
  FAQ_A2:
    '<b>Coaching</b>: You are fundamentally psychologically stable but want to work on your goals faster, in a structured and targeted way, for example handling AI tools with confidence, putting impostor feelings into perspective or establishing digital routines (digital detox).<br/><br/><b>Psychotherapy</b>: When symptoms clearly limit you (sleep, appetite, mood, anxiety, compulsions, psychosomatic complaints) or conflicts keep recurring. I clarify this with you in the first session and recommend the setting that fits.',
  FAQ_Q3: 'How often do sessions take place?',
  FAQ_A3:
    '<b>Coaching</b>: flexibly, usually every one to three weeks, depending on your goal and pace.<br/><br/><b>Psychotherapy</b>: as a rule once or twice a week (more often in psychoanalysis). Continuity is part of what makes it effective.',
  FAQ_Q4: 'Does confidentiality also apply in coaching?',
  FAQ_A4:
    'Yes. <b>Confidentiality</b> is my standard, in <em>psychotherapy just as in coaching</em>. This confidentiality creates the safe space that sets me apart from many coaches: you can speak openly without anything you share being passed on.',
  FAQ_Q5: 'Individual or group: what do I offer?',
  FAQ_A5:
    'I work in an <b>individual setting</b> and also offer <b>groups</b>. For certain topics (e.g. procrastination or digital detox) a group can be very effective.',
  FAQ_Q6: 'What happens in the free first session?',
  FAQ_A6:
    'We clarify your concern, your goals and whether coaching or psychotherapy makes sense. You receive a recommendation for the setting, including frequency and approach, transparent and without obligation.',
  FAQ_Note:
    'Please note: coaching is <em>not</em> psychotherapy and does not replace treatment when symptoms have clinical significance. If in doubt, I clarify with you in the first session which setting fits.',

  // NonDisclosure section (src/components/sections/NonDisclosure.tsx)
  ND_Tag: 'Confidentiality',
  ND_Heading: 'Confidentiality &amp; protection of your privacy',
  ND_Intro:
    'Confidentiality applies with me <b>from the first to the last word we exchange</b>, regardless of whether we talk in person, by phone or in writing. I uphold it in psychotherapy as well as in coaching. <b>Because trust is the foundation of every good working relationship.</b>',
  ND_List1:
    'No content is passed on to third parties without your explicit prior consent.',
  ND_List2: 'The same standards for psychotherapy <b>and</b> coaching.',
  ND_CTA1: 'Arrange a first session',
  ND_CTA2: 'Learn more',
  ND_ImageAlt: 'Nadine Hauswirth doing groundwork with a horse',

  // Blog slider (src/components/sections/BlogSlider.tsx)
  Blog_Tag: 'Blog',
  Blog_Title: 'New from the blog',
  Blog_Empty: 'No blog posts found.',
  Blog_Prev: 'Previous',
  Blog_Next: 'Next',

  // Achievement counter (src/components/sections/AchivementCounter.tsx,
  // item labels passed as message ids from src/pages/index.tsx)
  Counter_Tag: 'Change Coaching',
  Counter_Headline: 'Many years of experience with therapy and coaching',
  Counter_Years: 'Years',
  Counter_Therapies: 'Therapy sessions',
  Counter_Coachings: 'Coaching sessions',
};

const catalogs: Record<string, Partial<typeof messagesDe>> = {
  de: messagesDe,
  en: messagesEn
};

/**
 * The catalog for a locale, with the German one underneath it. react-intl
 * falls back to `defaultMessage` for a missing key, and every call site
 * carries the German text as its defaultMessage, but merging here means an
 * English page never renders a German string that HAS a translation just
 * because the key was added to `de` and forgotten in `en`.
 */
export const getMessages = (locale: string): Record<string, string> => ({
  ...messagesDe,
  ...(catalogs[locale.split(/[-_]/)[0]?.toLowerCase() ?? ''] ?? {})
});
